/**
 * Authentication state.
 *
 * The session itself lives in an HttpOnly cookie the browser manages; this
 * context only mirrors *who* the server says we are. Nothing here is a
 * security control — every route the user reaches is re-authorized server-side
 * on each request. It exists so the UI can render the right thing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, endpoints, type Role, type User } from './api';

interface AuthState {
  user: User | null;
  /** True until the initial session probe finishes. */
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** Called when any request reports 401, so the UI drops to the login screen. */
  clear(): void;
  hasRole(...roles: Role[]): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Ask the server who we are on load: the cookie may still be valid from a
  // previous visit, and only the server can say.
  useEffect(() => {
    let cancelled = false;
    endpoints
      .session()
      .then((result) => {
        if (!cancelled) setUser(result.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await endpoints.login(email, password);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await endpoints.logout();
    } finally {
      // Whatever the server said, stop showing signed-in UI.
      setUser(null);
    }
  }, []);

  const clear = useCallback(() => setUser(null), []);

  const hasRole = useCallback(
    (...roles: Role[]) => Boolean(user && roles.includes(user.role)),
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, clear, hasRole }),
    [user, loading, login, logout, clear, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/**
 * Wrap an async action so an expired session drops the user to the login
 * screen rather than showing a confusing error.
 */
export function useApiErrorHandler() {
  const { clear } = useAuth();
  return useCallback(
    (error: unknown): string => {
      if (error instanceof ApiError) {
        if (error.isUnauthenticated) {
          clear();
          return 'Your session has expired. Please sign in again.';
        }
        return error.message;
      }
      return 'Something went wrong. Please try again.';
    },
    [clear],
  );
}
